import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SingleSelectDropdownComponent } from '../../../business-details/single-select-dropdown.component';
import { OffcanvasSheetComponent } from '../../../shared/offcanvas-sheet/offcanvas-sheet.component';

export interface BudgetLineItemForm {
  fiscalPeriodID: number | null;
  costCenterID: number | null;
  itemID: number | null;
  costCategory: string;
  budgetAmount: number | null;
  notes: string;
}

export interface BudgetFormPayload {
  budget: {
    budgetID: string;
    budgetName: string;
    fiscalYear: number;
    budgetType: string;
    status: string;
    description: string;
  };
  lineItems: BudgetLineItemForm[];
}

@Component({
  selector: 'app-budget-item-offcanvas',
  standalone: true,
  imports: [CommonModule, FormsModule, SingleSelectDropdownComponent, OffcanvasSheetComponent],
  templateUrl: './budget-item-offcanvas.component.html',
  styleUrls: [
    '../../../shared/offcanvas-sheet/offcanvas-sheet.shared.css',
    './budget-item-offcanvas.component.css',
  ],
})
export class BudgetItemOffcanvasComponent implements OnChanges {
  @ViewChildren('lineCard') lineCards!: QueryList<ElementRef<HTMLElement>>;
  @Input() visible = false;
  @Input() budget: any = null;
  @Input() isEditing = false;
  @Input() fiscalPeriods: any[] = [];
  @Input() months: { value: string; label: string }[] = [];
  @Input() quarters: { value: string; label: string }[] = [];
  @Input() years: { value: string; label: string }[] = [];

  @Output() closeCanvas = new EventEmitter<void>();
  @Output() save = new EventEmitter<BudgetFormPayload>();

  readonly Math = Math;
  readonly String = String;

  formData = {
    budgetID: '',
    budgetName: '',
    fiscalYear: new Date().getFullYear(),
    budgetType: 'Monthly',
    status: 'Draft',
    description: '',
  };

  selectedMonth: string | null = null;
  selectedQuarter: string | null = null;
  selectedYear: string | null = String(new Date().getFullYear());

  lineItems: BudgetLineItemForm[] = [];

  get budgetTypeOptions() {
    return [
      { value: 'Monthly',   label: 'Monthly' },
      { value: 'Quarterly', label: 'Quarterly' },
      { value: 'Annually',  label: 'Annually' },
    ];
  }

  get fiscalPeriodOptions(): { value: string; label: string }[] {
    return this.fiscalPeriods.map(p => {
      const raw = p.startDate || p.StartDate;
      let label: string;
      if (raw) {
        const d = new Date(raw);
        label = d.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
      } else {
        label = `${p.periodType ?? ''} ${p.periodNumber ?? ''} (${p.fiscalYear ?? ''})`.trim();
      }
      return { value: String(p.fiscalPeriodID), label };
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.visible && (changes['visible'] || changes['budget'])) {
      if (this.budget) {
        this.formData = {
          budgetID: this.budget.budgetID ?? '',
          budgetName: this.budget.budgetName ?? '',
          fiscalYear: this.budget.fiscalYear ?? new Date().getFullYear(),
          budgetType: this.budget.budgetType ?? 'Monthly',
          status: this.budget.status ?? 'Draft',
          description: this.budget.description ?? '',
        };
        this.selectedYear = String(this.formData.fiscalYear);
        this.selectedMonth = null;
        this.selectedQuarter = null;
        this.lineItems = (this.budget.lineItems ?? []).map((l: any) => ({ ...l }));
      } else {
        this.resetForm();
      }
    }
  }

  get title(): string {
    return this.isEditing ? 'Edit Budget' : 'Add Budget';
  }

  onBudgetTypeChange(v: string | null): void {
    this.formData.budgetType = v ?? 'Monthly';
    this.selectedMonth = null;
    this.selectedQuarter = null;
  }

  onYearChange(v: string | null): void {
    this.selectedYear = v;
    this.formData.fiscalYear = v ? Number(v) : new Date().getFullYear();
  }

  addLine(): void {
    const defaultPeriod = this.fiscalPeriods.length > 0 ? this.fiscalPeriods[0].fiscalPeriodID : null;
    this.lineItems.push({
      fiscalPeriodID: defaultPeriod,
      costCenterID: null,
      itemID: null,
      costCategory: '',
      budgetAmount: null,
      notes: '',
    });
    setTimeout(() => {
      this.lineCards.last?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  removeLine(idx: number): void {
    this.lineItems.splice(idx, 1);
  }

  onSave(): void {
    this.save.emit({
      budget: { ...this.formData },
      lineItems: this.lineItems.map(l => ({ ...l, itemID: null, costCategory: '' })),
    });
    this.onClose();
  }

  onClose(): void {
    this.closeCanvas.emit();
  }

  private resetForm(): void {
    this.formData = {
      budgetID: '',
      budgetName: '',
      fiscalYear: new Date().getFullYear(),
      budgetType: 'Monthly',
      status: 'Draft',
      description: '',
    };
    this.selectedMonth = null;
    this.selectedQuarter = null;
    this.selectedYear = String(new Date().getFullYear());
    this.lineItems = [];
  }
}
